import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { AUTH_ENDPOINTS } from '../../../constants/api';

interface CheckEmailStepProps {
  email: string;
}

const RESEND_COOLDOWN_SECONDS = 60;

const CheckEmailStep: React.FC<CheckEmailStepProps> = ({ email }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (isResending || cooldown > 0) return;

    setIsResending(true);
    setResendMessage(null);
    setResendError(null);

    try {
      await axios.post(AUTH_ENDPOINTS.RESEND_VERIFICATION, { email });
      setResendMessage('Verification email sent. Please check your inbox.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setResendError(
        err.response?.data?.message || 'Could not resend the verification email. Please try again shortly.'
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.paymentContainer}>
        <div className={styles.paymentIcon}>📧</div>
        <h2 className={styles.paymentTitle}>Check Your Email</h2>
        <p className={styles.formDescription}>
          We've sent a verification link to <strong>{email}</strong>. Click the link in that
          email to verify your account.
        </p>

        <div className={styles.reviewWarningSection}>
          <h4 className={styles.reviewWarningTitle}>What happens next</h4>
          <ul className={styles.reviewWarningList}>
            <li>Click the verification link in the email we just sent you.</li>
            <li>Log in with the email and password you just created.</li>
            <li>Choose a seat count and either start a free trial or subscribe.</li>
          </ul>
        </div>

        {resendMessage && (
          <div className={styles.successMessage} role="status">
            {resendMessage}
          </div>
        )}
        {resendError && (
          <div className={styles.errorMessage} role="alert">
            {resendError}
          </div>
        )}

        <div className={styles.buttonGroupSpaced}>
          <button
            type="button"
            onClick={handleResend}
            className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}
            disabled={isResending || cooldown > 0}
          >
            {isResending
              ? 'Sending...'
              : cooldown > 0
              ? `Resend available in ${cooldown}s`
              : "Didn't get the email? Resend"}
          </button>

          <Link to="/login" className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}>
            Go to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckEmailStep;